import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@dashboard/components/ui/alert';
import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';

// As long as a generated secret, so the row wraps the same way hidden and revealed.
const HIDDEN_SECRET = '•'.repeat(64);

const WebhookSigningSecret = ({ webhookId }) => {
  const { sellerId, token } = useAuth();
  const [copied, setCopied] = useState(false);
  const [confirmingRotation, setConfirmingRotation] = useState(false);

  // A mutation, not a query, so the secret never sits in the query cache.
  const revealSecret = useMutation({
    mutationFn: () => fetcher(`/sellers/${sellerId}/webhooks/${webhookId}/secret`, token),
    onError: error => {
      toast({
        title: 'Failed to load the signing secret',
        description: error?.message || 'Please try again',
        type: 'error',
      });
    },
  });

  const rotateSecret = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/webhooks/${webhookId}/secret/rotate`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      revealSecret.reset();
      setConfirmingRotation(false);
      toast({
        title: 'Signing secret rotated',
        description: 'Update your endpoint with the new secret.',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Failed to rotate the signing secret',
        description: error?.message || 'Please try again',
        type: 'error',
      });
    },
  });

  const secret = rotateSecret.data?.secret ?? revealSecret.data?.secret;

  const hideSecret = () => {
    revealSecret.reset();
    rotateSecret.reset();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 rounded border-1 border-gray-100 dark:border-border bg-gray-50 dark:bg-card py-2 pl-4 pr-2">
        <span className="min-w-0 flex-1 break-all font-mono text-sm text-gray-900 dark:text-foreground">
          {secret || <span aria-hidden="true">{HIDDEN_SECRET}</span>}
        </span>

        {secret ? (
          <>
            <CopyToClipboard
              text={secret}
              onCopy={() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1000);
              }}
            >
              <Button variant="ghost" size="icon" aria-label="Copy secret" title="Copy">
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </CopyToClipboard>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Hide secret"
              title="Hide"
              onClick={hideSecret}
            >
              <EyeOff className="size-4" />
            </Button>
          </>
        ) : (
          <>
            <span className="size-8 shrink-0" aria-hidden="true" />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Reveal secret"
              title="Reveal"
              disabled={revealSecret.isPending}
              onClick={() => revealSecret.mutate()}
            >
              <Eye className="size-4" />
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Rotate secret"
          title="Rotate"
          disabled={confirmingRotation}
          onClick={() => setConfirmingRotation(true)}
        >
          <RefreshCw className="size-4" />
        </Button>
      </div>

      {confirmingRotation && (
        <Alert variant="warning">
          <TriangleAlert />
          <AlertTitle>Rotate this signing secret?</AlertTitle>
          <AlertDescription>
            The current secret stops working right away and every delivery is signed with the new
            one. Update your endpoint with the new secret to keep accepting deliveries.
          </AlertDescription>
          <AlertAction className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmingRotation(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={rotateSecret.isPending}
              onClick={() => rotateSecret.mutate()}
            >
              Rotate now
            </Button>
          </AlertAction>
        </Alert>
      )}
    </div>
  );
};

export default WebhookSigningSecret;
